import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Affix,
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Modal,
  Rate,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import {
  DownloadOutlined,
  ExportOutlined,
  FilePdfOutlined,
  FileSearchOutlined,
  LinkOutlined,
  ReloadOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import api from '../config/api';

dayjs.extend(isoWeek);

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

type Frequency = 'weekly' | 'biweekly' | 'monthly';

function defaultPeriodForFrequency(frequency: Frequency): [Dayjs, Dayjs] {
  if (frequency === 'monthly') {
    const start = dayjs().subtract(1, 'month').startOf('month');
    const end = dayjs().subtract(1, 'month').endOf('month');
    return [start, end];
  }
  if (frequency === 'biweekly') {
    const end = dayjs().subtract(1, 'day').startOf('day');
    const start = end.subtract(13, 'day').startOf('day');
    return [start, end];
  }
  const start = dayjs().startOf('isoWeek').subtract(1, 'week');
  const end = start.endOf('isoWeek');
  return [start, end];
}

function normalizeSupervisorScore(score: number | undefined): number | null {
  if (score == null || score === 0 || Number.isNaN(score)) return null;
  if (score < 1 || score > 5) return null;
  return score;
}

function formatResolveDuration(createdAt?: string, resolvedAt?: string | null): string {
  if (!createdAt) return '—';
  if (!resolvedAt) return 'Not solved yet (no resolved date)';
  const a = new Date(createdAt).getTime();
  const b = new Date(resolvedAt).getTime();
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) return '—';
  const ms = b - a;
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (h > 48) {
    const d = Math.floor(h / 24);
    return `${d}d ${h % 24}h`;
  }
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

interface AuditableUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  last_audit_at?: string | null;
  last_period_start?: string | null;
  last_period_end?: string | null;
}

interface SampleTicket {
  id: string;
  ticket_number: string;
  subject: string;
  status: string;
  business_name?: string;
  feedback_has_row?: boolean;
  feedback_is_submitted?: boolean;
  feedback_rating?: number | null;
  feedback_comment?: string | null;
  feedback_submitted_at?: string | null;
}

interface GradeRow {
  strengths: string;
  improvements: string;
  score: number | undefined;
}

interface AuditConfig {
  frequency: Frequency;
  sample_size: number;
  target_roles: string[];
}

const TicketAudit: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const historySectionRef = useRef<HTMLDivElement>(null);
  const [agentId, setAgentId] = useState<string | undefined>();
  const [period, setPeriod] = useState<[Dayjs, Dayjs] | null>(null);
  const [sampled, setSampled] = useState<SampleTicket[]>([]);
  const [grades, setGrades] = useState<Record<string, GradeRow>>({});
  const [summaryNotes, setSummaryNotes] = useState('');
  const [sampling, setSampling] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detailReport, setDetailReport] = useState<{ report: any; items: any[] } | null>(null);
  const [lastSavedReportId, setLastSavedReportId] = useState<string | null>(null);
  const [saveBannerVisible, setSaveBannerVisible] = useState(false);

  const [previewTicketId, setPreviewTicketId] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewTicket, setPreviewTicket] = useState<any>(null);
  const [previewComments, setPreviewComments] = useState<any[]>([]);
  const [previewFields, setPreviewFields] = useState<any[]>([]);

  const [exportMonth, setExportMonth] = useState<Dayjs | null>(dayjs().subtract(1, 'month'));
  const [exportAgentId, setExportAgentId] = useState<string>('all');
  const [exportBusy, setExportBusy] = useState<string | null>(null);

  const [analyticsRange, setAnalyticsRange] = useState<[Dayjs, Dayjs] | null>(() => {
    const end = dayjs();
    const start = end.subtract(89, 'day');
    return [start, end];
  });

  const { data: config, isLoading: configLoading } = useQuery(
    'ticket-audit-config',
    async () => {
      const res = await api.get('/api/ticket-audits/config');
      return res.data as AuditConfig;
    }
  );

  const { data: auditableData, isLoading: auditableLoading } = useQuery(
    'ticket-audit-auditable-users',
    async () => {
      const res = await api.get('/api/ticket-audits/auditable-users');
      return res.data as { users: AuditableUser[] };
    }
  );

  const auditableUsers = auditableData?.users || [];
  const sampleSize = config?.sample_size ?? 5;

  const frequency = (config?.frequency || 'weekly') as Frequency;

  useEffect(() => {
    if (!configLoading && config && !period) {
      setPeriod(defaultPeriodForFrequency(frequency));
    }
  }, [configLoading, config, frequency, period]);

  const applyFrequencyDefaults = () => {
    setPeriod(defaultPeriodForFrequency(frequency));
    message.info('Period reset to match the configured cadence');
  };

  const analyticsFrom = analyticsRange?.[0]?.format('YYYY-MM-DD');
  const analyticsTo = analyticsRange?.[1]?.format('YYYY-MM-DD');

  const { data: analyticsData, isLoading: analyticsLoading } = useQuery(
    ['ticket-audit-analytics', analyticsFrom, analyticsTo],
    async () => {
      const res = await api.get('/api/ticket-audits/analytics', {
        params: { from: analyticsFrom, to: analyticsTo },
      });
      return res.data as {
        by_agent: Array<{
          agent_id: string;
          agent_first_name: string;
          agent_last_name: string;
          avg_score: string | number | null;
          item_count: number;
          report_count: number;
        }>;
        by_month: Array<{ month_key: string; report_count: number; avg_score: string | number | null }>;
      };
    },
    { enabled: !!analyticsFrom && !!analyticsTo }
  );

  const barChartData = useMemo(() => {
    return (analyticsData?.by_agent || []).map((r) => ({
      name: `${r.agent_first_name || ''} ${r.agent_last_name || ''}`.trim() || r.agent_id,
      avgScore: r.avg_score != null ? Number(r.avg_score) : null,
      reports: r.report_count,
      items: r.item_count,
    }));
  }, [analyticsData]);

  const lineChartData = useMemo(() => {
    return (analyticsData?.by_month || []).map((r) => ({
      month: r.month_key,
      reports: r.report_count,
      avgScore: r.avg_score != null ? Number(r.avg_score) : null,
    }));
  }, [analyticsData]);

  const runSample = async () => {
    if (!agentId || !period?.[0] || !period[1]) {
      message.warning('Select an agent and audit period');
      return;
    }
    setSampling(true);
    try {
      const res = await api.post('/api/ticket-audits/sample', {
        agent_id: agentId,
        period_start: period[0].format('YYYY-MM-DD'),
        period_end: period[1].format('YYYY-MM-DD'),
      });
      const tickets: SampleTicket[] = res.data.tickets || [];
      const limit = res.data.requested_limit ?? sampleSize;
      setSampled(tickets);
      const nextGrades: Record<string, GradeRow> = {};
      tickets.forEach((t) => {
        nextGrades[t.id] = { strengths: '', improvements: '', score: undefined };
      });
      setGrades(nextGrades);
      setSaveBannerVisible(false);
      setLastSavedReportId(null);
      if (tickets.length < limit) {
        message.warning(
          `Only ${tickets.length} ticket(s) matched this agent and period (need activity in range, status other than New).`
        );
      } else {
        message.success(`Loaded ${tickets.length} random tickets`);
      }
    } catch (e: any) {
      message.error(e.response?.data?.error || 'Failed to sample tickets');
    } finally {
      setSampling(false);
    }
  };

  const saveReport = async () => {
    if (!agentId || !period?.[0] || !period[1]) {
      message.warning('Select an agent and period');
      return;
    }
    if (!sampled.length) {
      message.warning('Pick random tickets first');
      return;
    }
    const items = sampled.map((t) => {
      const g = grades[t.id] || { strengths: '', improvements: '', score: undefined };
      return {
        ticket_id: t.id,
        strengths: g.strengths || null,
        improvements: g.improvements || null,
        score: normalizeSupervisorScore(g.score),
      };
    });
    setSaving(true);
    try {
      const res = await api.post('/api/ticket-audits/reports', {
        agent_id: agentId,
        period_start: period[0].format('YYYY-MM-DD'),
        period_end: period[1].format('YYYY-MM-DD'),
        summary_notes: summaryNotes || null,
        items,
      });
      const id = res.data?.id as string | undefined;
      setLastSavedReportId(id || null);
      setSaveBannerVisible(true);
      message.success('Audit report saved');
      setSampled([]);
      setGrades({});
      setSummaryNotes('');
      queryClient.invalidateQueries('ticket-audit-reports');
      queryClient.invalidateQueries('ticket-audit-auditable-users');
      queryClient.invalidateQueries('ticket-audit-analytics');
      setTimeout(() => {
        historySectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (e: any) {
      const err = e.response?.data?.error || e.message || 'Failed to save report';
      message.error(typeof err === 'string' ? err : 'Failed to save report');
      console.error('[TicketAudit] save failed', e.response?.status, e.response?.data);
    } finally {
      setSaving(false);
    }
  };

  const { data: reportsData, isLoading: reportsLoading } = useQuery(
    'ticket-audit-reports',
    async () => {
      const res = await api.get('/api/ticket-audits/reports', { params: { page: 1, limit: 50 } });
      return res.data as { reports: any[]; total: number };
    }
  );

  const openDetail = async (id: string) => {
    try {
      const res = await api.get(`/api/ticket-audits/reports/${id}`);
      setDetailReport(res.data);
    } catch (e: any) {
      message.error(e.response?.data?.error || 'Failed to load report');
    }
  };

  const openTicketPreview = async (ticketId: string) => {
    setPreviewTicketId(ticketId);
    setPreviewLoading(true);
    setPreviewTicket(null);
    setPreviewComments([]);
    setPreviewFields([]);
    try {
      const [tRes, cRes, fRes] = await Promise.all([
        api.get(`/api/tickets/${ticketId}`),
        api.get(`/api/tickets/${ticketId}/comments`),
        api.get(`/api/tickets/${ticketId}/custom-fields`),
      ]);
      setPreviewTicket(tRes.data);
      setPreviewComments(cRes.data?.comments || []);
      setPreviewFields(fRes.data?.fields || []);
    } catch (e: any) {
      message.error(e.response?.data?.error || 'Failed to load ticket');
      setPreviewTicketId(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const runExportCsv = async () => {
    if (!exportMonth) {
      message.warning('Select a month');
      return;
    }
    const y = exportMonth.year();
    const m = exportMonth.month() + 1;
    setExportBusy('csv');
    try {
      const res = await api.get('/api/ticket-audits/export', {
        params: { year: y, month: m, agent_id: exportAgentId },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ticket-audits-detail-${y}-${String(m).padStart(2, '0')}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      message.success('CSV download started');
    } catch (e: any) {
      await handleExportError(e);
    } finally {
      setExportBusy(null);
    }
  };

  const runExportDetailXlsx = async () => {
    if (!exportMonth) {
      message.warning('Select a month');
      return;
    }
    const y = exportMonth.year();
    const m = exportMonth.month() + 1;
    setExportBusy('detail-xlsx');
    try {
      const res = await api.get('/api/ticket-audits/export', {
        params: { year: y, month: m, agent_id: exportAgentId, format: 'json' },
      });
      const rows = res.data?.rows || [];
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Audit lines');
      XLSX.writeFile(wb, `ticket-audits-detail-${y}-${String(m).padStart(2, '0')}.xlsx`);
      message.success('Excel file saved');
    } catch (e: any) {
      message.error(e.response?.data?.error || 'Export failed');
    } finally {
      setExportBusy(null);
    }
  };

  const runExportSummaryXlsx = async () => {
    if (!exportMonth) {
      message.warning('Select a month');
      return;
    }
    const y = exportMonth.year();
    const m = exportMonth.month() + 1;
    setExportBusy('summary-xlsx');
    try {
      const res = await api.get('/api/ticket-audits/export-summary', {
        params: { year: y, month: m, agent_id: exportAgentId },
      });
      const rows = res.data?.rows || [];
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Agents audited');
      XLSX.writeFile(wb, `ticket-audits-summary-${y}-${String(m).padStart(2, '0')}.xlsx`);
      message.success('Summary Excel saved');
    } catch (e: any) {
      message.error(e.response?.data?.error || 'Export failed');
    } finally {
      setExportBusy(null);
    }
  };

  async function handleExportError(e: any) {
    if (e.response?.data instanceof Blob) {
      const text = await e.response.data.text();
      try {
        const j = JSON.parse(text);
        message.error(j.error || 'Export failed');
      } catch {
        message.error('Export failed');
      }
    } else {
      message.error(e.response?.data?.error || 'Export failed');
    }
  }

  const downloadAnalyticsExcel = () => {
    if (!analyticsData) return;
    const wb = XLSX.utils.book_new();
    const ws1 = XLSX.utils.json_to_sheet(
      (analyticsData.by_agent || []).map((r) => ({
        agent: `${r.agent_first_name || ''} ${r.agent_last_name || ''}`.trim(),
        agent_id: r.agent_id,
        avg_score: r.avg_score,
        item_count: r.item_count,
        report_count: r.report_count,
      }))
    );
    XLSX.utils.book_append_sheet(wb, ws1, 'By agent');
    const ws2 = XLSX.utils.json_to_sheet(analyticsData.by_month || []);
    XLSX.utils.book_append_sheet(wb, ws2, 'By month');
    const fn = `ticket-audit-analytics-${analyticsFrom}_to_${analyticsTo}.xlsx`;
    XLSX.writeFile(wb, fn);
    message.success('Analytics Excel saved');
  };

  const downloadAnalyticsPdf = () => {
    if (!analyticsData) return;
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(14);
    doc.text('Ticket audit analytics', 14, 16);
    doc.setFontSize(10);
    doc.text(`Range: ${analyticsFrom} to ${analyticsTo}`, 14, 24);
    autoTable(doc, {
      startY: 30,
      head: [['Agent', 'Avg score', 'Items', 'Reports']],
      body: (analyticsData.by_agent || []).map((r) => [
        `${r.agent_first_name || ''} ${r.agent_last_name || ''}`.trim(),
        r.avg_score != null ? String(r.avg_score) : '—',
        String(r.item_count),
        String(r.report_count),
      ]),
    });
    const yAfter = (doc as any).lastAutoTable?.finalY || 40;
    autoTable(doc, {
      startY: yAfter + 10,
      head: [['Month', 'Reports', 'Avg score']],
      body: (analyticsData.by_month || []).map((r) => [
        r.month_key,
        String(r.report_count),
        r.avg_score != null ? String(r.avg_score) : '—',
      ]),
    });
    doc.save(`ticket-audit-analytics-${analyticsFrom}_to_${analyticsTo}.pdf`);
    message.success('PDF saved');
  };

  const feedbackBlock = (t: SampleTicket | any) => {
    if (!t.feedback_has_row) {
      return <Text type="secondary">No customer feedback record for this ticket.</Text>;
    }
    if (!t.feedback_is_submitted) {
      return <Text type="warning">Feedback was requested but not submitted yet.</Text>;
    }
    return (
      <Space direction="vertical" size={0}>
        <div>
          <Text type="secondary">Customer rating: </Text>
          {t.feedback_rating != null ? (
            <>
              <Rate disabled allowHalf={false} value={t.feedback_rating} count={5} />
              <Tag color="blue" style={{ marginLeft: 8 }}>
                {t.feedback_rating}/5
              </Tag>
            </>
          ) : (
            <Text type="secondary">Submitted (no numeric rating)</Text>
          )}
        </div>
        {t.feedback_comment ? (
          <Paragraph style={{ marginBottom: 0 }} ellipsis={{ rows: 3, expandable: true }}>
            {t.feedback_comment}
          </Paragraph>
        ) : null}
        {t.feedback_submitted_at && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            Submitted {new Date(t.feedback_submitted_at).toLocaleString()}
          </Text>
        )}
      </Space>
    );
  };

  const agentSelectOptions = useMemo(() => {
    return auditableUsers.map((u) => {
      let hint = 'No audit yet';
      if (u.last_audit_at) {
        hint = `Last audit ${new Date(u.last_audit_at).toLocaleDateString()}`;
        if (u.last_period_start && u.last_period_end) {
          hint += ` (${u.last_period_start} → ${u.last_period_end})`;
        }
      }
      return {
        value: u.id,
        label: `${u.first_name} ${u.last_name} (${u.role}) — ${hint}`,
      };
    });
  }, [auditableUsers]);

  const historyColumns = useMemo(
    () => [
      {
        title: 'Created',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 160,
        render: (v: string) => new Date(v).toLocaleString(),
      },
      {
        title: 'Agent',
        key: 'agent',
        render: (_: any, r: any) =>
          `${r.agent_first_name || ''} ${r.agent_last_name || ''}`.trim() || '—',
      },
      {
        title: 'Period',
        key: 'period',
        render: (_: any, r: any) => `${r.period_start} → ${r.period_end}`,
      },
      {
        title: 'Auditor',
        key: 'auditor',
        render: (_: any, r: any) =>
          `${r.auditor_first_name || ''} ${r.auditor_last_name || ''}`.trim() || '—',
      },
      { title: 'Tickets', dataIndex: 'item_count', key: 'item_count', width: 80 },
      {
        title: '',
        key: 'act',
        width: 100,
        render: (_: any, r: any) => (
          <Button type="link" size="small" onClick={() => openDetail(r.id)}>
            View
          </Button>
        ),
      },
    ],
    []
  );

  const fieldValueDisplay = (v: unknown) => {
    if (v == null) return '—';
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
  };

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <Title level={2}>
        <FileSearchOutlined style={{ marginRight: 12 }} />
        Ticket Audit
      </Title>
      <Paragraph type="secondary">
        Sample tickets for an agent, grade in this workspace only, and use customer feedback as context.
        Defaults come from Settings → Ticket audit.
      </Paragraph>

      {saveBannerVisible && lastSavedReportId && (
        <Alert
          style={{ marginBottom: 16 }}
          type="success"
          showIcon
          closable
          onClose={() => setSaveBannerVisible(false)}
          message={
            <span>
              Report saved (id: <Text code>{lastSavedReportId}</Text>). See <strong>Recent reports</strong>{' '}
              below.
            </span>
          }
        />
      )}

      <Card title="New audit" style={{ marginBottom: 24 }}>
        {configLoading ? (
          <Text type="secondary">Loading configuration…</Text>
        ) : (
          <Text type="secondary">
            Cadence: <Tag>{frequency}</Tag> · Sample size: <Tag>{sampleSize}</Tag> · Roles:{' '}
            <Tag>{(config?.target_roles || []).join(', ') || 'agent, developer'}</Tag>
            <Button type="link" size="small" onClick={applyFrequencyDefaults}>
              Reset period to match cadence
            </Button>
          </Text>
        )}
        <Divider style={{ margin: '12px 0' }} />
        <Row gutter={[16, 16]}>
          <Col xs={24} md={8}>
            <Text strong>Agent</Text>
            <Select
              showSearch
              allowClear
              placeholder={auditableLoading ? 'Loading…' : 'Select agent'}
              style={{ width: '100%', marginTop: 8 }}
              optionFilterProp="label"
              loading={auditableLoading}
              value={agentId}
              onChange={(v) => setAgentId(v)}
              options={agentSelectOptions}
            />
          </Col>
          <Col xs={24} md={10}>
            <Text strong>Audit period</Text>
            <Paragraph type="secondary" style={{ margin: '4px 0 0', fontSize: 12 }}>
              Weekly = previous ISO week; bi-weekly = last 14 days; monthly = previous calendar month when you
              reset from Settings cadence.
            </Paragraph>
            <DatePicker.RangePicker
              style={{ width: '100%', marginTop: 8 }}
              value={period || undefined}
              onChange={(r) => setPeriod(r as [Dayjs, Dayjs] | null)}
            />
          </Col>
          <Col xs={24} md={6} style={{ display: 'flex', alignItems: 'flex-end' }}>
            <Space wrap>
              <Button icon={<ReloadOutlined />} loading={sampling} onClick={runSample}>
                Pick {sampleSize} random tickets
              </Button>
            </Space>
          </Col>
        </Row>

        {sampled.length > 0 && (
          <>
            <Divider />
            <Text strong>Summary notes (optional)</Text>
            <TextArea
              style={{ marginTop: 8, marginBottom: 16 }}
              rows={2}
              value={summaryNotes}
              onChange={(e) => setSummaryNotes(e.target.value)}
              placeholder="Overall notes for this audit session"
            />
            {sampled.map((t) => (
              <Card
                key={t.id}
                size="small"
                style={{ marginBottom: 16 }}
                title={
                  <Space>
                    <span>
                      #{t.ticket_number} — {t.subject}
                    </span>
                    <Button type="link" size="small" icon={<FileSearchOutlined />} onClick={() => openTicketPreview(t.id)}>
                      Preview ticket
                    </Button>
                  </Space>
                }
              >
                <Row gutter={16}>
                  <Col xs={24} md={10}>
                    <Text type="secondary">Business: {t.business_name || '—'}</Text>
                    <br />
                    <Text type="secondary">Status: {t.status}</Text>
                    <Divider orientation="left" plain style={{ fontSize: 12 }}>
                      Customer feedback
                    </Divider>
                    {feedbackBlock(t)}
                    <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
                      Use the above when scoring; your grade is recorded only in this audit.
                    </Text>
                  </Col>
                  <Col xs={24} md={14}>
                    <Form layout="vertical">
                      <Form.Item label="What went well">
                        <TextArea
                          rows={2}
                          value={grades[t.id]?.strengths || ''}
                          onChange={(e) =>
                            setGrades((g) => ({
                              ...g,
                              [t.id]: {
                                ...g[t.id],
                                strengths: e.target.value,
                                improvements: g[t.id]?.improvements || '',
                                score: g[t.id]?.score,
                              },
                            }))
                          }
                        />
                      </Form.Item>
                      <Form.Item label="Needs improvement">
                        <TextArea
                          rows={2}
                          value={grades[t.id]?.improvements || ''}
                          onChange={(e) =>
                            setGrades((g) => ({
                              ...g,
                              [t.id]: {
                                strengths: g[t.id]?.strengths || '',
                                improvements: e.target.value,
                                score: g[t.id]?.score,
                              },
                            }))
                          }
                        />
                      </Form.Item>
                      <Form.Item label="Supervisor score (1–5, optional)">
                        <Rate
                          allowClear
                          count={5}
                          value={grades[t.id]?.score}
                          onChange={(v) =>
                            setGrades((g) => ({
                              ...g,
                              [t.id]: {
                                strengths: g[t.id]?.strengths || '',
                                improvements: g[t.id]?.improvements || '',
                                score: v,
                              },
                            }))
                          }
                        />
                      </Form.Item>
                    </Form>
                  </Col>
                </Row>
              </Card>
            ))}
            <Affix offsetBottom={16}>
              <div style={{ background: '#fff', padding: 12, border: '1px solid #f0f0f0', borderRadius: 8 }}>
                <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={saveReport} size="large">
                  Save audit report
                </Button>
              </div>
            </Affix>
          </>
        )}
      </Card>

      <Card title="Dashboard" style={{ marginBottom: 24 }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Space wrap align="center">
            <Text strong>Date range</Text>
            <DatePicker.RangePicker
              value={analyticsRange || undefined}
              onChange={(r) => setAnalyticsRange(r as [Dayjs, Dayjs] | null)}
            />
            <Button icon={<ExportOutlined />} onClick={downloadAnalyticsExcel} disabled={!analyticsData}>
              Excel
            </Button>
            <Button icon={<FilePdfOutlined />} onClick={downloadAnalyticsPdf} disabled={!analyticsData}>
              PDF
            </Button>
          </Space>
          {analyticsLoading ? (
            <Spin />
          ) : (
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <Text strong>Average score by agent</Text>
                <div style={{ width: '100%', height: 320, marginTop: 8 }}>
                  <ResponsiveContainer>
                    <BarChart data={barChartData} layout="vertical" margin={{ left: 8, right: 16 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" domain={[0, 5]} />
                      <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="avgScore" name="Avg score" fill="#1677ff" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Col>
              <Col xs={24} lg={12}>
                <Text strong>Activity by month</Text>
                <div style={{ width: '100%', height: 320, marginTop: 8 }}>
                  <ResponsiveContainer>
                    <LineChart data={lineChartData} margin={{ left: 8, right: 16 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" domain={[0, 5]} />
                      <Tooltip />
                      <Legend />
                      <Line yAxisId="left" type="monotone" dataKey="reports" name="Reports" stroke="#52c41a" />
                      <Line yAxisId="right" type="monotone" dataKey="avgScore" name="Avg score" stroke="#1677ff" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Col>
            </Row>
          )}
        </Space>
      </Card>

      <Card title="Monthly export" style={{ marginBottom: 24 }}>
        <Paragraph type="secondary" style={{ marginTop: 0 }}>
          Period overlap: audits whose <strong>audit period</strong> intersects the selected month. Choose one
          agent or all.
        </Paragraph>
        <Space wrap align="start">
          <div>
            <Text strong>Month</Text>
            <br />
            <DatePicker
              picker="month"
              style={{ marginTop: 8, minWidth: 160 }}
              value={exportMonth}
              onChange={(v) => setExportMonth(v)}
            />
          </div>
          <div>
            <Text strong>Agent</Text>
            <br />
            <Select
              style={{ width: 280, marginTop: 8 }}
              value={exportAgentId}
              onChange={setExportAgentId}
            >
              <Option value="all">All agents</Option>
              {auditableUsers.map((a) => (
                <Option key={a.id} value={a.id}>
                  {a.first_name} {a.last_name}
                </Option>
              ))}
            </Select>
          </div>
          <Space direction="vertical" style={{ marginTop: 28 }} size={8}>
            <Button icon={<DownloadOutlined />} loading={exportBusy === 'csv'} onClick={runExportCsv}>
              CSV (detail lines)
            </Button>
            <Button icon={<ExportOutlined />} loading={exportBusy === 'detail-xlsx'} onClick={runExportDetailXlsx}>
              Excel (detail lines)
            </Button>
            <Button icon={<ExportOutlined />} loading={exportBusy === 'summary-xlsx'} onClick={runExportSummaryXlsx}>
              Excel (who was audited)
            </Button>
          </Space>
        </Space>
      </Card>

      <div ref={historySectionRef}>
        <Card title="Recent reports">
          <Table
            rowKey="id"
            loading={reportsLoading}
            dataSource={reportsData?.reports || []}
            columns={historyColumns}
            pagination={false}
            locale={{ emptyText: 'No saved audits yet' }}
          />
        </Card>
      </div>

      <Modal
        title="Audit report detail"
        open={!!detailReport}
        onCancel={() => setDetailReport(null)}
        footer={null}
        width={800}
      >
        {detailReport && (
          <>
            <Paragraph>
              <Text strong>Agent: </Text>
              {detailReport.report.agent_first_name} {detailReport.report.agent_last_name}
              <br />
              <Text strong>Period: </Text>
              {detailReport.report.period_start} → {detailReport.report.period_end}
              <br />
              <Text strong>Auditor: </Text>
              {detailReport.report.auditor_first_name} {detailReport.report.auditor_last_name}
            </Paragraph>
            {detailReport.report.summary_notes ? (
              <Alert type="info" message={detailReport.report.summary_notes} style={{ marginBottom: 16 }} />
            ) : null}
            {(detailReport.items || []).map((it: any) => (
              <Card key={it.id} size="small" style={{ marginBottom: 12 }} title={`#${it.ticket_number}`}>
                <Paragraph style={{ marginBottom: 8 }}>{feedbackBlock(it)}</Paragraph>
                <Text strong>Strengths: </Text>
                <Paragraph>{it.strengths || '—'}</Paragraph>
                <Text strong>Improvements: </Text>
                <Paragraph>{it.improvements || '—'}</Paragraph>
                <Text strong>Score: </Text> {it.score != null ? `${it.score}/5` : '—'}
              </Card>
            ))}
          </>
        )}
      </Modal>

      <Modal
        title={previewTicket ? `Ticket ${previewTicket.ticket_number}` : 'Ticket preview'}
        open={!!previewTicketId}
        onCancel={() => {
          setPreviewTicketId(null);
          setPreviewTicket(null);
        }}
        footer={null}
        width={720}
      >
        {previewLoading ? (
          <Spin />
        ) : previewTicket ? (
          <Tabs
            items={[
              {
                key: 'summary',
                label: 'Summary',
                children: (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Paragraph style={{ marginBottom: 0 }}>
                      <Text strong>Subject: </Text>
                      {previewTicket.subject}
                    </Paragraph>
                    <Paragraph style={{ marginBottom: 0 }}>
                      <Text strong>Status: </Text> {previewTicket.status}
                    </Paragraph>
                    <Paragraph style={{ marginBottom: 0 }}>
                      <Text strong>Priority: </Text> {previewTicket.priority || '—'}
                    </Paragraph>
                    <Paragraph style={{ marginBottom: 0 }}>
                      <Text strong>Category: </Text> {previewTicket.category_name || '—'}
                    </Paragraph>
                    <Paragraph style={{ marginBottom: 0 }}>
                      <Text strong>Created: </Text> {new Date(previewTicket.created_at).toLocaleString()}
                    </Paragraph>
                    <Paragraph style={{ marginBottom: 0 }}>
                      <Text strong>Resolved: </Text>{' '}
                      {previewTicket.resolved_at
                        ? new Date(previewTicket.resolved_at).toLocaleString()
                        : '—'}
                    </Paragraph>
                    <Paragraph style={{ marginBottom: 0 }}>
                      <Text strong>Time to solve: </Text>
                      {formatResolveDuration(previewTicket.created_at, previewTicket.resolved_at)}
                    </Paragraph>
                    <Paragraph>
                      <Text strong>Description</Text>
                      <div style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}>{previewTicket.description || '—'}</div>
                    </Paragraph>
                    <Button
                      type="primary"
                      icon={<LinkOutlined />}
                      onClick={() => navigate(`/workspace/${previewTicket.id}`)}
                    >
                      Open in workspace
                    </Button>
                  </Space>
                ),
              },
              {
                key: 'comments',
                label: `Comments (${previewComments.length})`,
                children: (
                  <div style={{ maxHeight: 360, overflow: 'auto' }}>
                    {previewComments.length === 0 ? (
                      <Text type="secondary">No comments</Text>
                    ) : (
                      previewComments.map((c: any) => (
                        <Card key={c.id} size="small" style={{ marginBottom: 8 }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {new Date(c.created_at).toLocaleString()}
                            {c.is_internal ? <Tag style={{ marginLeft: 8 }}>Internal</Tag> : null}
                          </Text>
                          <Paragraph style={{ marginBottom: 0, marginTop: 8, whiteSpace: 'pre-wrap' }}>
                            {c.comment}
                          </Paragraph>
                        </Card>
                      ))
                    )}
                  </div>
                ),
              },
              {
                key: 'fields',
                label: 'Custom fields',
                children: (
                  <Table
                    size="small"
                    pagination={false}
                    rowKey="id"
                    dataSource={previewFields}
                    columns={[
                      { title: 'Field', dataIndex: 'label', key: 'label' },
                      {
                        title: 'Value',
                        dataIndex: 'value',
                        key: 'value',
                        render: (_: any, row: any) => fieldValueDisplay(row.value),
                      },
                    ]}
                  />
                ),
              },
            ]}
          />
        ) : null}
      </Modal>
    </div>
  );
};

export default TicketAudit;
